import { useFeatureFlagEnabled } from 'posthog-js/react';
import { createBrowserRouter, createRoutesFromElements, Outlet, Route } from 'react-router';

import { FWDCURVE_CATALOG_PATH, SPOT_CATALOG_PATH } from '@/constants/constants';
import PersonalizationVariantRenderer from '@/features/ComponentVariants/PersonalizationVariantRenderer';
import { MaintenanceMode } from '@/features/MaintenanceMode';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import Catalog from '@/pages/Catalog';
import DataHub from '@/pages/DataHub';
import Expired from '@/pages/Expired';
import { FACTSHEET_PARAM_NAME } from '@/pages/Facthsheets/constants';
import Home from '@/pages/Home';
import Logout from '@/pages/Logout';
import Lookup from '@/pages/Lookup';
import Markets from '@/pages/Markets';
import NotFound from '@/pages/NotFound';
import Login from '@/pages/Onboarding/features/Login';
import OnboardingForm from '@/pages/Onboarding/OnboardingForm';
import Profile from '@/pages/Profile';
import ManageSubscription from '@/pages/Subscription';
import SubscriptionSuccess from '@/pages/Subscription/components/SubscriptionSuccess';

import CookieBanner from './components/CookieBanner';
import GlobalModal from './components/GlobalModal';
import SymbolSelector from './components/SymbolSelector';
import { MAINTENANCE_MODE_FLAG } from './constants/featureFlags';
import { WidgetAnimationProvider } from './features/Dashboard/context/WidgetAnimation/provider';
import Listeners from './features/Listeners';
import { TrackingLister } from './features/Tracking';
import { IdentifyPostHogProvider } from './features/Tracking/context/PostHogContext';
import FileDownload from './pages/FileDownload';
import Onboarding from './pages/Onboarding';
import Welcome from './pages/Welcome';

const MaintenanceGate = () => {
  const isMaintenanceMode = useFeatureFlagEnabled(MAINTENANCE_MODE_FLAG);
  if (isMaintenanceMode) {
    return <MaintenanceMode />;
  }

  return <Outlet />; // normal app when not in maintenance mode
};

const Wrapper = () => {
  return (
    <IdentifyPostHogProvider>
      <Listeners />
      <SymbolSelector />

      <GlobalModal />
      <CookieBanner />
      <WidgetAnimationProvider>
        <TrackingLister>
          <MaintenanceGate />
        </TrackingLister>
      </WidgetAnimationProvider>
    </IdentifyPostHogProvider>
  );
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Wrapper />}>
      <Route
        element={<Onboarding />}
        path='/onboarding'
      />
      <Route
        element={<Login />}
        path='/login'
      />
      <Route
        element={<OnboardingForm />}
        path='/onboarding/form'
      />
      <Route
        element={<Logout />}
        path='/logout'
      />
      <Route
        element={<Expired />}
        path='/expired'
      />

      <Route element={<PublicLayout />}>
        <Route
          index
          element={<Home />}
        />
        <Route
          element={<Home />}
          path='/token'
        />
        <Route
          element={<Lookup />}
          path={`/:${FACTSHEET_PARAM_NAME}`}
        />
        <Route element={<AuthLayout />}>
          <Route
            element={<Welcome />}
            path='/welcome'
          />
          <Route
            element={<FileDownload />}
            path='/file/:fileName/:fileId'
          />
          <Route element={<AppLayout />}>
            <Route
              element={<Markets />}
              path='/markets'
            />

            <Route
              element={<Markets />}
              path='/markets/:marketId'
            />
            <Route
              element={<Markets />}
              path='markets/:marketId/:tabId'
            />
            <Route
              element={<Catalog />}
              path={`/catalog/${SPOT_CATALOG_PATH}`}
            />
            <Route
              element={<Catalog />}
              path={`/catalog/${FWDCURVE_CATALOG_PATH}`}
            />
            <Route
              element={<DataHub />}
              path='/data-hub'
            >
              <Route path='*' />
            </Route>
            <Route
              element={<Profile />}
              path='/profile'
            />
            <Route
              element={<ManageSubscription />}
              path='/manage-subscription'
            />
            <Route
              element={<PersonalizationVariantRenderer componentName='dashboard_route_element' />}
              path='/dashboard/:dashboardId?/:tabName?'
            />
          </Route>
          <Route
            element={<SubscriptionSuccess />}
            path='/subscription/success'
          />
        </Route>
      </Route>
      <Route
        element={<NotFound />}
        path='/notfound'
      />
      <Route
        element={<NotFound />}
        path='/:section/*'
      />
      <Route
        element={<NotFound />}
        path='*'
      />
      {process.env.NODE_ENV === 'development' && (
        <Route
          element={<MaintenanceMode />}
          path='test-page'
        />
      )}
    </Route>
  )
);
